import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box, Chip, CircularProgress, IconButton, Paper, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useGetCompanyOverviewQuery } from "../../../Api/platformApi";
import {
  ProfileTab, BrandingTab, NumbersTab, IntegrationsTab, ApiAccessTab,
  SubscriptionTab, UsageTab, UsersTab, SecurityTab,
} from "./tabs";

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Company Control Center — the full tenant profile at /companies/:id.
 *
 * One aggregate fetch hydrates every tab. Super admins see all 9 tabs; company
 * admins see only Profile, Branding, Subscription, Usage and Users (provider
 * settings and internal costs are hidden).
 */
const CompanyProfile: React.FC = () => {
  const { id } = useParams();
  const companyId = Number(id);
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetCompanyOverviewQuery(companyId, { skip: !companyId });

  const [isSuper, setIsSuper] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.post(`${API_URL}/user/userInfo`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    }).then((res) => { if (!cancelled) setIsSuper(Boolean(res.data?.data?.is_super_admin)); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const tabs = useMemo(() => {
    const all = [
      { label: "Company Profile", Comp: ProfileTab, superOnly: false },
      { label: "Branding", Comp: BrandingTab, superOnly: false },
      { label: "Phone Numbers", Comp: NumbersTab, superOnly: true },
      { label: "Integrations", Comp: IntegrationsTab, superOnly: true },
      { label: "API Access", Comp: ApiAccessTab, superOnly: true },
      { label: "Subscription", Comp: SubscriptionTab, superOnly: false },
      { label: "Usage", Comp: UsageTab, superOnly: false },
      { label: "Users", Comp: UsersTab, superOnly: false },
      { label: "Security", Comp: SecurityTab, superOnly: true },
    ];
    return all.filter((t) => isSuper || !t.superOnly);
  }, [isSuper]);

  useEffect(() => { if (tab >= tabs.length) setTab(0); }, [tabs, tab]);

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}><CircularProgress /></Box>;
  }
  if (isError || !data?.data) {
    return <Box sx={{ p: 3 }}><Typography color="error">Company not found.</Typography></Box>;
  }

  const c = data.data.company;
  const Active = tabs[tab]?.Comp ?? ProfileTab;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
        <IconButton onClick={() => navigate("/platform/companies")}><ArrowBackIcon /></IconButton>
        <Box>
          <Typography variant="h5" fontWeight={700}>{c.name}</Typography>
          <Typography variant="body2" color="text.secondary">/{c.slug} · {c.business_type || "—"}</Typography>
        </Box>
        <Chip label={c.status} color={c.status === "active" ? "success" : c.status === "suspended" ? "error" : "default"} sx={{ ml: 1 }} />
      </Stack>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {tabs.map((t) => <Tab key={t.label} label={t.label} />)}
        </Tabs>
      </Paper>

      <Active companyId={companyId} data={data.data} isSuper={isSuper} />
    </Box>
  );
};

export default CompanyProfile;
